import {
  cargoAmountToSystemUnit,
  getCargoUnitQuantity,
} from '../../constants/cargoUnits';
import type { PaymentUnit } from '../../graphql';
import type { LocationId } from '../core/Core';
import { stringToNumOpt } from '../core/util';

export const FALLBACK_COA_CARGO_UNIT = 'mts';

export function getOrderCoaDetails<
  CoaAssortment extends {
    assortmentId: number | null;
    assortmentGroupId: number | null;
    id: number;
    minAmount?: number | string | null;
    maxAmount?: number | string | null;
    paymentUnit: PaymentUnit;
  },
  CoaRoute extends {
    id: number;
    originId: number | null;
    originZoneId: number | null | undefined;
    originZoneLocations: Set<number> | null;
    destinationId: number | null;
    destinationZoneId: number | null | undefined;
    destinationZoneLocations: Set<number> | null;
  },
  CoaFreight extends {
    id: number;
    coaAssortmentId: number | null;
    routeId?: number | null;
  },
>(
  order: {
    assortmentId: number | null;
    assortmentGroupId: number | null;
    loadPortId: LocationId | null;
    dischargePortId: LocationId | null;
  },
  plannedAmount: {
    mass: number;
    volume: number;
  },
  coa: {
    cargoUnitName: string | null;
    coaAssortments: CoaAssortment[];
    freights: CoaFreight[];
    routes: CoaRoute[];
  },
  routeId: number | null | undefined,
): {
  coaAssortment: CoaAssortment | null;
  coaFreight: CoaFreight | null;
  coaRoute: CoaRoute | null;
  errors: {
    /** True if assortment could be matched with a different amount */
    invalidAmount: boolean;
  };
} {
  const cargoUnit = coa.cargoUnitName ?? FALLBACK_COA_CARGO_UNIT;
  const quantity = getCargoUnitQuantity(cargoUnit) ?? 'mass';
  const orderAmount = plannedAmount[quantity];

  let matchedByGroup = false;
  let coaAssortment: CoaAssortment | null = null;
  let someAmountInvalid = false;

  for (const ca of coa.coaAssortments) {
    const isCatchAll =
      ca.assortmentId === null && ca.assortmentGroupId === null;

    const assortmentGroupMatches =
      ca.assortmentGroupId !== null &&
      ca.assortmentGroupId === order.assortmentGroupId;

    const assortmentMatches =
      ca.assortmentId !== null && ca.assortmentId === order.assortmentId;

    const assortmentMinAmount = stringToNumOpt(ca.minAmount);
    const assortmentMaxAmount = stringToNumOpt(ca.maxAmount);

    const minAmount = assortmentMinAmount
      ? cargoAmountToSystemUnit(assortmentMinAmount, cargoUnit)
      : -Infinity;
    const maxAmount = assortmentMaxAmount
      ? cargoAmountToSystemUnit(assortmentMaxAmount, cargoUnit)
      : Infinity;

    const amountMatches = orderAmount >= minAmount && orderAmount <= maxAmount;

    if (assortmentMatches && amountMatches) {
      // Break out if we find an exact match
      coaAssortment = ca;
      break;
    }

    if (assortmentGroupMatches && amountMatches) {
      matchedByGroup = true;
      coaAssortment = ca;
      continue;
    }

    if (!matchedByGroup && isCatchAll && amountMatches) {
      coaAssortment = ca;
    }

    /* If assortment could be matched with a proper amount, set a flag so we can provide user feedback in UI */
    if (
      (assortmentMatches || assortmentGroupMatches || isCatchAll) &&
      !amountMatches
    ) {
      someAmountInvalid = true;
    }
  }

  let coaRoute: CoaRoute | null = null;

  if (routeId) {
    coaRoute = coa.routes.find((cr) => cr.id === routeId) ?? null;
  } else {
    for (const cr of coa.routes) {
      const loadPortMatches =
        order.loadPortId && cr.originId && order.loadPortId === cr.originId;
      const loadZoneMatches =
        order.loadPortId &&
        cr.originZoneId &&
        cr.originZoneLocations?.has(order.loadPortId);
      const dischargePortMatches =
        order.dischargePortId &&
        cr.destinationId &&
        order.dischargePortId === cr.destinationId;
      const dischargeZoneMatches =
        order.dischargePortId &&
        cr.destinationZoneId &&
        cr.destinationZoneLocations?.has(order.dischargePortId);

      /* We can stop searching on an exact port - port match as that rule has the highest priority */
      if (loadPortMatches && dischargePortMatches) {
        coaRoute = cr;
        break;
      }

      if (
        (loadPortMatches || loadZoneMatches) &&
        (dischargePortMatches || dischargeZoneMatches)
      ) {
        coaRoute = cr;
      }
    }
  }

  const coaFreight =
    coaAssortment !== null && coaRoute !== null
      ? (coa.freights.find((price) => {
          return (
            coaAssortment !== null &&
            price.coaAssortmentId === coaAssortment.id &&
            coaRoute !== null &&
            price.routeId === coaRoute.id
          );
        }) ?? null)
      : null;

  return {
    coaAssortment,
    coaRoute,
    coaFreight,
    errors: {
      invalidAmount: !coaAssortment && someAmountInvalid,
    },
  };
}
